<template>
  <c-container
    v-if="loading"
    padding-size="small"
  >
    <c-progress-bar :progress="aiAnswerProgress" />
    <div
      v-if="gettingAIAnswers"
      :style="{ color: $colors.grey.base, textAlign: 'center', marginTop: '2rem' }"
    >
      Waiting for GPT-3 to answer. This might take up to 1 minute.<br><br>

      Pro Tip: Select relevant text to reduce the amount of tokens sent to Open AI.
    </div>
  </c-container>
  <div
    v-else
    :style="{ overflowY: 'auto', overflowX: 'hidden', height: 'calc(100vh - 4.8rem - 7.45rem)', background: 'white' }"
  >
    <c-container
      v-if="usesProFeatures && hasDisabledFeatures"
      padding-size="small"
    >
      <upgrade-hint
        :uses-a-i-prefills="aiProperties.length > 0"
        :uses-blocks="template.blocks.length > 0"
        uses-storage
      />
    </c-container>
    <div
      v-if="thumbnail !== '' && !ignoreThumbail"
      style="width: 100%; cursor: inherit;"
      class="_thumbnail"
    >
      <div style="display: grid; width: 100%; height: 20vh;">
        <div style="grid-area: 1 / 1; width: 100%; height: 100%;">
          <img
            :src="thumbnail"
            referrerpolicy="same-origin"
            style="display: block; object-fit: cover; border-radius: 0; width: 100%; height: 20vh; opacity: 1; object-position: center 50%;"
          >
        </div>
      </div>
      <div class="_actions">
        <c-button
          icon="delete"
          outlined
          fab
          @click="removeThumbnail"
        />
      </div>
    </div>
    <c-container
      padding-size="small"
    >
      <c-list
        distance="small"
      >
        <div
          v-for="(property, id, idx) in properties"
          :key="idx"
        >
          <property-use
            :property="{ ...property, loading: propertiesLoading[id] }"
            :value-to-send="valuesToSend[id].value"
            @update:value="change($event, id)"
            @cancel-click="propertiesLoading[id] = false"
          />
        </div>
      </c-list>
    </c-container>
    <div :style="{ padding: '0 2rem' }">
      <c-divider size="none" />
    </div>
    <c-container
      padding-size="small"
    >
      <div>
        <div
          v-if="bodyLoading"
          :style="{ display: 'flex', justifyContent: 'center', color: $colors.grey.base }"
        >
          <c-icon
            type="loading"
            :style="{ marginRight: '.6rem'}"
            spin
            :color="$colors.grey.lighten2"
          />
          Loading...
        </div>
        <tip-tap
          ref="editor"
          :value="content"
          :style="bodyLoading ? { visibility: 'hidden' } : {}"
          class="page-body"
          :auto-focus="addBodyContent"
          :can-upload-images="hasStorage"
          @update:value="handleEditorUpdate"
        />
      </div>
    </c-container>
  </div>
  <div
    v-if="stepRecording"
    class="_overlay"
  >
    <div class="_inner">
      <div class="_timer">
        Steps recorded: {{ count }}
      </div>
      <div class="_buttons">
        <c-button
          icon="square"
          fab
          outlined
          :disabled="canUploadScreenshot"
          :style="{ marginRight: '.8rem' }"
          @click="stopStepRecording"
        />
      </div>
    </div>
  </div>
  <div
    v-if="timer !== ''"
    class="_overlay"
  >
    <div class="_inner">
      <div class="_timer">
        {{ timer }}
      </div>
      <div class="_buttons">
        <c-button
          icon="square"
          fab
          outlined
          :disabled="canUploadScreenshot"
          :style="{ marginRight: '.8rem' }"
          @click="startVideoRecording"
        />
        <c-button
          fab
          icon="delete"
          color="light"
          outlined
          :style="{ marginRight: '.8rem' }"
          @click="cancelVideoRecording"
        />
      </div>
    </div>
  </div>
  <c-container
    padding-size="small"
    :style="{ borderTop: `1px solid ${$colors.grey.lighten3}`, background: 'white' }"
  >
    <div :style="{ display: 'flex' }">
      <c-button
        icon="screenshot"
        fab
        :disabled="canUploadScreenshot"
        outlined
        :style="{ marginRight: '.8rem' }"
        @click="addScreenshot"
      />
      <c-button
        icon="ordered-list"
        fab
        :disabled="canUploadScreenshot"
        outlined
        :style="{ marginRight: '.8rem' }"
        @click="startStepRecorder"
      />
      <c-button
        :icon="videoRecording ? 'square' : 'video-camera'"
        fab
        :disabled="canUploadScreenshot"
        outlined
        :style="{ marginRight: '.8rem' }"
        @click="startVideoRecording"
      />
      <c-button
        color="primary"
        stretch
        :loading="sendDataLoading"
        @click="sendData"
      >
        Save
      </c-button>
    </div>
  </c-container>
</template>

<script>
import CButton from 'shared/components/CButton.vue';
import CContainer from 'shared/components/CContainer.vue';
import CDivider from 'shared/components/CDivider.vue';
import CIcon from 'shared/components/CIcon.vue';
import CList from 'shared/components/CList.vue';
import CProgressBar from 'shared/components/CProgressBar.vue';
import TipTap from 'shared/components/TipTap.vue';
import { BETA_PHASE_MANAGER,
  C_BLOCK_TYPE_AI,
  C_BLOCK_TYPE_COOKIES,
  C_BLOCK_TYPE_LOCAL_STORAGE,
  C_BLOCK_TYPE_PAGE_URL,
  C_BLOCK_TYPE_SELECTION,
  C_BLOCK_TYPE_TEXT,
  C_BLOCK_TYPE_VISIBLE_AREA_SCREENSHOT,
  featureFlags,
  FIREBASE_APP,
  HISTORY_SERVICE,
  NOTION_DATABASE_SERVICE,
  TEMPLATE_SERVICE,
  USER_SERVICE,
  WORKSPACE_SERVICE } from 'shared/constants';
import {
  NOTION_PROPERTY_CHECKBOX, NOTION_PROPERTY_DATE,
  NOTION_PROPERTY_EMAIL, NOTION_PROPERTY_MULTI_SELECT, NOTION_PROPERTY_NUMBER,
  NOTION_PROPERTY_PHONE_NUMBER, NOTION_PROPERTY_SELECT,
  NOTION_PROPERTY_STATUS,
  NOTION_PROPERTY_TYPE_RICH_TEXT, NOTION_PROPERTY_TYPE_TITLE, NOTION_PROPERTY_URL,
} from 'shared/lib/notion-constants';
import useFeatureAccess from 'shared/services/feature-access';
import { httpsCallable } from 'shared/services/firebase';
import reportHandledError from 'shared/services/report-error';
import useStorage from 'shared/services/storage';
import useSubscription from 'shared/services/subscription';
import { computed, inject } from 'vue';

import PropertyUse from '@/components/PropertyUse.vue';
import UpgradeHint from '@/components/UpgradeHint.vue';
import { prosemirrorToNotion } from '@/lib/prosemirror-to-notion';

export default {
  name: 'UseTemplate',
  props: {
    selection: {
      type: String,
      default: '',
    },
    selectionText: {
      type: String,
      default: '',
    },
    pageUrl: {
      type: String,
      default: 'https://www.youtube.com/watch?v=XxCXIQ1LekM',
    },
    bodyText: {
      type: String,
      default: '',
    },
    bodyHtml: {
      type: String,
      default: '',
    },
    thumbnail: {
      type: String,
      default: '',
    },
    pageTitle: {
      type: String,
      default: '',
    },
    cookie: {
      type: String,
      default: '',
    },
    localStorage: {
      type: String,
      default: '',
    },
    parentHost: {
      type: String,
      default: '',
    },
  },
  components: {
    UpgradeHint,
    CIcon,
    CProgressBar,
    PropertyUse,
    CContainer,
    CButton,
    CList,
    CDivider,
    TipTap,
  },
  data() {
    return {
      videoRecording: false,
      timer: '',
      takingScreenshot: false,
      bodyLoading: true,
      properties: {},
      valuesToSend: {},
      loading: true,
      // Whether the user has started filling this capture in. Once they have, a
      // tab switch must not rebuild the page underneath them.
      composerTouched: false,
      // Fingerprint of the content we last wrote ourselves, so the editor's own
      // echoes are not mistaken for user edits. Starts as an empty document.
      lastAppliedContent: '[]',
      pageDataReceived: false,
      addBodyContent: false,
      content: { type: 'doc', content: [] },
      sendDataLoading: false,
      screenshots: [],
      screenshotTaken: null,
      aiAnswers: {},
      propertiesLoading: {},
      gettingAIAnswers: false,
      aiAnswerProgress: 0,
      bodyProgress: 0,
      ignoreThumbail: false,
      count: 0,
      stepRecording: false,
    };
  },
  computed: {
    cover() {
      if (this.thumbnail === '' || this.ignoreThumbail) {
        return null;
      }
      return {
        type: 'external',
        external: { url: this.thumbnail },
      };
    },
    aiProperties() {
      return Object.keys(this.template.properties).reduce((res, next) => {
        if (this.template.properties[next].prefill !== 'ai') {
          return res;
        }
        res.push(next);
        return res;
      }, []);
    },
    upgradeUrl() {
      return `${import.meta.env.VITE_WEBSITE_URL}/app/plans`;
    },
    usesProFeatures() {
      return this.aiProperties.length > 0 || this.hasAIBlocks || this.template.blocks.length > 0 || !this.hasStorage;
    },
    hasAIBlocks() {
      for (let i = 0; i < this.template.blocks.length; i++) {
        if (this.template.blocks[i].type === C_BLOCK_TYPE_AI) {
          return true;
        }
      }
      return false;
    },
    text() {
      if (this.selection !== '') {
        return this.selectionText;
      }
      return this.bodyText;
    },
    canUseBlocks() {
      return this.hasFeatureAccess(featureFlags.pageBlocks);
    },
    canUseAIProperties() {
      return this.hasFeatureAccess(featureFlags.aiProperties);
    },
  },
  setup() {
    const firebaseApp = inject(FIREBASE_APP);
    const userService = inject(USER_SERVICE);
    const historySvc = inject(HISTORY_SERVICE);

    const notionDatabaseService = inject(NOTION_DATABASE_SERVICE);
    const workspaceService = inject(WORKSPACE_SERVICE);
    const templateService = inject(TEMPLATE_SERVICE);
    const { uploadScreenshot, uploadScreenRecording } = useStorage(firebaseApp.storage, userService.currentUser);
    const betaPhaseManager = inject(BETA_PHASE_MANAGER);
    const {
      currentPlan,
      subscriptionPastDue,
      hasStorage,
    } = useSubscription(userService.currentUser, betaPhaseManager);
    const { hasFeatureAccess } = useFeatureAccess(currentPlan, subscriptionPastDue, betaPhaseManager);
    const canUseBlocks = computed(() => hasFeatureAccess(featureFlags.pageBlocks));
    const canUseAIProperties = computed(() => hasFeatureAccess(featureFlags.aiProperties));
    const hasDisabledFeatures = computed(() => (!canUseAIProperties.value) || (!canUseBlocks.value) || (!hasStorage.value));
    return {
      hasStorage,
      template: templateService.selectedTemplate,
      notionDatabaseService,
      uploadScreenshot,
      uploadScreenRecording,
      canUploadScreenshot: !hasStorage.value,
      userService,
      hasFeatureAccess,
      hasDisabledFeatures,
      historySvc,
      workspaceId: workspaceService.selectedWorkspace.value.id,
    };
  },
  emits: ['leave', 'go-to-success-page'],
  methods: {
    stopStepRecording() {
      this.stepRecording = false;
      window.parent.postMessage({ msg: 'NOTIX_STOP_STEP_RECORDING' }, '*');
    },
    updateTimer(startTime) {
      const twoDigitFormat = (n) => {
        if (n < 10) {
          return `0${n}`;
        }

        return `${n}`;
      };

      const formatDuration = (seconds) => {
        const minutes = parseInt(seconds / 60, 10);
        if (minutes === 0) {
          return `0:${twoDigitFormat(seconds)}`;
        }
        return `${minutes}:${twoDigitFormat(seconds - (minutes * 60))}`;
      };

      const update = () => {
        if (!this.videoRecording) {
          return;
        }
        const d = new Date();
        const diff = (d.getTime() - startTime.getTime()) / 1000;

        this.timer = formatDuration(Math.round(diff));
        setTimeout(update, 1000);
      };
      update();
    },
    removeThumbnail() {
      this.ignoreThumbail = true;
    },
    prosemirrorToNotion,
    startAiProgress(key) {
      this[key] = 0;
      const updateProgress = () => {
        this[key] += 100 / 60;
        setTimeout(() => {
          if (this[key] >= 100) {
            return;
          }
          updateProgress();
        }, 1000);
      };
      setTimeout(() => {
        updateProgress();
      }, 1000);
    },
    // TipTap emits update:value from its own mount as well as from the editor's
    // internal onUpdate, neither of which goes through applyContent, and it
    // normalises an empty document into a single empty paragraph on the way out.
    // A flag around our own calls therefore misses both, so compare the content
    // instead - ignoring that normalisation.
    contentFingerprint(doc) {
      const nodes = ((doc || {}).content || [])
        .filter((node) => !(node.type === 'paragraph' && node.content === undefined));
      return JSON.stringify(nodes);
    },
    applyContent(res) {
      // Record before writing: the editor emits update:value during setContent,
      // so storing this afterwards means the echo arrives with nothing to match
      // against and is mistaken for a user edit.
      this.lastAppliedContent = this.contentFingerprint(res);
      this.$refs.editor.setContent(res);
    },
    handleEditorUpdate(value) {
      this.content = value;
      if (this.contentFingerprint(value) === this.lastAppliedContent) {
        return; // our own write coming back
      }
      // Erring towards "touched" is safe: the worst case is a capture that does
      // not follow a tab switch. The opposite mistake discards the user's work.
      this.composerTouched = true;
    },
    refreshFromPage() {
      // The first response is the initial load and always applies.
      if (!this.pageDataReceived) {
        this.pageDataReceived = true;
        this.initiateTemplate();
        return;
      }
      // Later responses mean the user switched tabs while the panel stayed open.
      // Only adopt the new page if they have not started filling this one in.
      if (this.composerTouched) {
        return;
      }
      // The parent updates text/pageUrl/... in this same tick; let the props
      // propagate before rebuilding from them.
      this.$nextTick(() => {
        this.initiateTemplate();
        this.evaluateBlocks();
      });
    },
    showAiError(message) {
      this.$eventBus.emit('show-toast', { type: 'error', message });
    },
    async getAnswers() {
      if (!this.canUseAIProperties || this.aiProperties.length === 0) {
        return;
      }
      this.startAiProgress('aiAnswerProgress');
      const getAIAnswer = httpsCallable(this.$functions, 'getAIAnswer');
      this.aiProperties.forEach(async (key) => {
        this.propertiesLoading[key] = true;
        try {
          const res = await getAIAnswer({
            property: this.template.properties[key],
            text: this.text,
          });
          if (!this.propertiesLoading[key]) {
            return;
          }
          if (res.data !== null && res.data !== undefined && res.data.error !== undefined) {
            this.showAiError(res.data.error);
            this.propertiesLoading[key] = false;
            return;
          }
          if (res.data !== null && res.data !== undefined) {
            this.aiAnswers[key] = res.data;
            this.computeValuesToSend({ [key]: this.template.properties[key] });
          }
        } catch (e) {
          reportHandledError(e, { where: 'ai property prefill', property: key });
          this.showAiError('Could not generate the AI prefill. Please try again.');
        }
        this.propertiesLoading[key] = false;
      });
    },
    async evaluateBlocks() {
      const res = {
        type: 'doc',
        content: [],
      };
      if (!this.canUseBlocks || this.template.blocks === undefined || this.template.blocks.length === 0) {
        if (this.selection !== '') {
          const parsedSelection = this.$refs.editor.parseHTML(this.selection);
          res.content.push(...parsedSelection.content);
          this.applyContent(res);
        }
        this.bodyLoading = false;
        return;
      }

      for (let i = 0; i < this.template.blocks.length; i++) {
        switch (this.template.blocks[i].type) {
          case C_BLOCK_TYPE_TEXT:
            res.content.push(...this.template.blocks[i].textContent.content);
            break;
          case C_BLOCK_TYPE_PAGE_URL:
            if (this.pageUrl === '') {
              break;
            }
            res.content.push({
              type: 'paragraph',
              content: [
                {
                  type: 'text',
                  marks: [{ type: 'link', attrs: { href: this.pageUrl } }],
                  text: this.pageUrl,
                },
              ],
            });
            break;
          case C_BLOCK_TYPE_SELECTION: {
            const parsedSelection = this.$refs.editor.parseHTML(this.selection);
            res.content.push(...parsedSelection.content);
            break;
          }
          case C_BLOCK_TYPE_VISIBLE_AREA_SCREENSHOT: {
            if (!this.hasStorage) {
              this.$eventBus.emit('show-toast', {
                type: 'error',
                message: 'Error while taking a screenshot because your storage is full. Please upgrade to upload more images.',
              });
              break;
            }
            /* eslint-disable */
            try {
              const {src, name, size} = await this.addScreenshotFromVisibleArea();
              /* eslint-enable */
              res.content.push({
                type: 'image',
                attrs: {
                  src,
                  name,
                  size,
                },
              });
            } catch (e) {
              // Silent until now: the screenshot simply did not appear.
              reportHandledError(e, { where: 'screenshot block' });
            }
            break;
          }
          case C_BLOCK_TYPE_AI: {
            const prefillBody = httpsCallable(this.$functions, 'prefillBody');
            if (this.template.blocks[i].prompt === '') {
              continue;
            }
            try {
              this.startAiProgress('bodyProgress');
              // eslint-disable-next-line no-await-in-loop
              const result = await prefillBody({ prompt: this.template.blocks[i].prompt, text: this.text });
              if (typeof result.data !== 'string') {
                this.showAiError(result.data?.error ?? 'Could not generate the AI content. Please try again.');
                break;
              }
              result.data.split('\n').forEach((t) => {
                if (t === '') {
                  return;
                }
                res.content.push({
                  type: 'paragraph',
                  content: [
                    {
                      type: 'text',
                      text: t,
                    },
                  ],
                });
              });
            } catch (e) {
              // Silent until now: the AI block simply came out empty.
              reportHandledError(e, { where: 'ai page block' });
            }
            break;
          }
          case C_BLOCK_TYPE_COOKIES: {
            if (this.cookie !== '') {
              res.content.push({
                type: 'codeBlock',
                attrs: { language: null },
                content: [{ type: 'text', text: `Cookie:\n\n${this.cookie}` }],
              });
            }
            break;
          }
          case C_BLOCK_TYPE_LOCAL_STORAGE: {
            if (this.localStorage !== '') {
              res.content.push({
                type: 'codeBlock',
                attrs: { language: null },
                content: [{ type: 'text', text: `Local storage:\n\n${this.localStorage}` }],
              });
            }
            break;
          }
          default:
        }
      }
      this.bodyLoading = false;
      this.applyContent(res);
    },
    handleMessage(event) {
      if (event.data.id === 'NOTIX_READY_RESPONSE') {
        this.refreshFromPage();
      }
      switch (event.data.msg) {
        case 'NOTIX_ADD_SCREENSHOT_CANCELLED': {
          this.takingScreenshot = false;
          document.removeEventListener('keydown', this.addVisibleScreenshotSpace);
          break;
        }
        case 'NOTIX_CONTENT_SCRIPT_INJECTED': {
          if (this.stepRecording) {
            this.startStepRecorder();
          }
          break;
        }
        case 'NOTIX_ADD_SCREENSHOT': {
          this.takingScreenshot = false;
          document.removeEventListener('keydown', this.addVisibleScreenshotSpace);
          this.uploadScreenshot(event.data.src).then(({ src, name, size }) => {
            if (this.screenshotTaken !== null) {
              this.screenshotTaken({ src, name, size });
              this.screenshotTaken = null;
              return;
            }
            this.$refs.editor.addImage({ src, name, size });
          });
          break;
        }
        case 'NOTIX_VIDEO_CANCELED': {
          this.videoRecording = false;
          break;
        }
        case 'NOTIX_VIDEO_STARTED': {
          const startTime = new Date();
          setTimeout(this.updateTimer(startTime), 1000);
          break;
        }
        case 'NOTIX_ADD_STEPS': {
          let content = '';
          const getTitle = (step) => {
            if (step.targetLabel === undefined) {
              return 'Click here';
            }
            const label = step.targetLabel.trim().replace(/\n|\r/g, ' ');
            if (label === '') {
              return 'Click here';
            }
            return `Click on ${label}`;
          };

          event.data.steps.forEach((step, index) => {
            content += `<h3>${this.count + index + 1}. ${getTitle(step)}</h3>`;
            content += `<img src="${step.imageUrl}">`;
            content += '</p>';
          });
          this.count += event.data.steps.length;
          this.$refs.editor.insertContent(content);
          break;
        }
        case 'NOTIX_ADD_VIDEO': {
          this.videoRecording = false;
          this.uploadScreenRecording(event.data.src).then(({ src, name, size }) => {
            this.$refs.editor.addVideo({ src, name, size });
          });
          break;
        }
        default:
      }
    },
    async cancelVideoRecording() {
      this.videoRecording = false;
      window.parent.postMessage({ msg: 'NOTIX_CANCEL_VIDEO_RECORDING' }, '*');
    },
    async startVideoRecording() {
      if (this.videoRecording) {
        window.parent.postMessage({ msg: 'NOTIX_STOP_VIDEO_RECORDING' }, '*');
        this.videoRecording = false;
        return;
      }
      this.videoRecording = true;
      window.parent.postMessage({ msg: 'NOTIX_START_VIDEO_RECORDING' }, '*');
    },
    addVisibleScreenshotSpace(event) {
      if (event.code === 'Space' && this.takingScreenshot) {
        window.parent.postMessage({ msg: 'NOTIX_TAKE_SCREENSHOT', options: { hidePrompt: true } }, '*');
      }
    },
    startStepRecorder() {
      window.parent.postMessage({ msg: 'NOTIX_RECORD_STEPS' }, '*');
      this.stepRecording = true;
    },
    addScreenshot() {
      this.takingScreenshot = true;
      document.addEventListener('keydown', this.addVisibleScreenshotSpace);
      window.blur();
      window.parent.postMessage({ msg: 'NOTIX_TAKE_SCREENSHOT' }, '*');
    },
    async addScreenshotFromVisibleArea() {
      window.parent.postMessage({ msg: 'NOTIX_TAKE_SCREENSHOT', options: { hidePrompt: true } }, '*');
      return new Promise((resolve) => {
        this.screenshotTaken = resolve;
      });
    },
    async initiateTemplate() {
      const properties = JSON.parse(JSON.stringify(this.template.properties));
      const titleProperty = properties.title;
      delete properties.title;
      this.properties = { title: titleProperty, ...properties };

      await this.computeValuesToSend(this.properties);
      this.loading = false;
    },
    async computeValue(property, aiAnswer) {
      if (property.prefill === 'ai' && aiAnswer !== undefined) {
        return aiAnswer;
      }

      if (property.prefill === 'predefined') {
        if (property.type === 'checkbox') {
          return [property.checked];
        }
        return [...property.predefined];
      }

      if (property.prefill === 'relative') {
        const d = new Date();
        const num = property.signTime * property.numTime;
        switch (property.unityTime) {
          case 'day':
            d.setDate(d.getDate() + num);
            break;
          case 'month':
            d.setMonth(d.getMonth() + num);
            break;
          case 'year':
            d.setFullYear(d.getFullYear() + num);
            break;
          default:
        }

        // Format from the local date, not toISOString(): that converts to UTC
        // first, so for anyone east of Greenwich a "today" prefill wrote
        // yesterday's date for most of their working day.
        const pad = (n) => String(n).padStart(2, '0');
        return [`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`];
      }

      if (property.prefill === 'url') {
        return [this.pageUrl];
      }

      if (property.prefill === 'page title') {
        return [this.pageTitle];
      }

      if (property.prefill === 'manual') {
        if (property.type === 'checkbox') {
          return [false];
        }
        return [];
      }

      return [];
    },
    async computeValuesToSend(properties) {
      const idsProperties = Object.keys(properties);
      for (let i = 0; i < idsProperties.length; i++) {
        const currentId = idsProperties[i];
        const property = properties[currentId];
        // eslint-disable-next-line no-await-in-loop
        const valueToSend = await this.computeValue(property, this.aiAnswers[currentId]);
        let valueEndDate = [''];

        if (property.type === 'date' && property.endTime) {
          // eslint-disable-next-line no-await-in-loop
          valueEndDate = await this.computeValue(property.endDate);
        }

        this.valuesToSend[currentId] = { value: valueToSend, endDate: valueEndDate };
      }
    },
    change(value, id, isEndDate = false) {
      this.composerTouched = true;
      if (isEndDate) {
        this.valuesToSend[id].endDate = value;
      } else {
        this.valuesToSend[id].value = value;
      }
    },
    toggleEndDate(id) {
      this.properties[id].endTime = !this.properties[id].endTime;
    },
    writeProperty(property, valueToSend) {
      if (valueToSend.value[0] === undefined || valueToSend.value[0] === '') {
        return 'empty';
      }

      if (property.type === NOTION_PROPERTY_TYPE_TITLE) {
        return { [NOTION_PROPERTY_TYPE_TITLE]: [{ text: { content: valueToSend.value[0] } }] };
      }

      if (property.type === NOTION_PROPERTY_TYPE_RICH_TEXT) {
        return { [NOTION_PROPERTY_TYPE_RICH_TEXT]: [{ text: { content: valueToSend.value[0] } }] };
      }

      if (property.type === NOTION_PROPERTY_NUMBER) {
        return { [NOTION_PROPERTY_NUMBER]: Number(valueToSend.value[0]) };
      }

      if (property.type === NOTION_PROPERTY_SELECT) {
        return { [NOTION_PROPERTY_SELECT]: { name: valueToSend.value[0] } };
      }

      if (property.type === NOTION_PROPERTY_MULTI_SELECT) {
        const selections = [];
        valueToSend.value.forEach((val) => {
          selections.push(
            { name: val },
          );
        });
        return { [NOTION_PROPERTY_MULTI_SELECT]: selections };
      }

      if (property.type === NOTION_PROPERTY_DATE) {
        const dates = { start: valueToSend.value[0], end: null };
        if (property.endTime) {
          dates.end = valueToSend.endDate[0];
        }

        return { [NOTION_PROPERTY_DATE]: dates };
      }

      if (property.type === NOTION_PROPERTY_URL) {
        return { [NOTION_PROPERTY_URL]: valueToSend.value[0] };
      }

      if (property.type === NOTION_PROPERTY_CHECKBOX) {
        return { [NOTION_PROPERTY_CHECKBOX]: valueToSend.value[0] };
      }

      if (property.type === NOTION_PROPERTY_EMAIL) {
        return { [NOTION_PROPERTY_EMAIL]: valueToSend.value[0] };
      }

      if (property.type === NOTION_PROPERTY_STATUS) {
        return { [NOTION_PROPERTY_STATUS]: { name: valueToSend.value[0] } };
      }

      if (property.type === NOTION_PROPERTY_PHONE_NUMBER) {
        return { [NOTION_PROPERTY_PHONE_NUMBER]: valueToSend.value[0] };
      }

      return {};
    },
    // Templates stored before the 2025-09-03 migration only know their database
    // id. The backfill adds dataSourceId to all of them, so this is the bridge
    // for anything captured between the deploy and the backfill finishing, and
    // for a template created by an older client afterwards.
    async resolveDataSourceId() {
      const database = await this.notionDatabaseService.getSingleNoParent(this.template.database.id);
      return database?.dataSourceId;
    },
    async sendData() {
      this.sendDataLoading = true;
      const propertiesToSend = {};

      const idsProperties = Object.keys(this.properties);
      for (let i = 0; i < idsProperties.length; i++) {
        const currentId = idsProperties[i];
        const writeable = this.writeProperty(this.properties[currentId], this.valuesToSend[currentId]);
        if (writeable !== 'empty') {
          propertiesToSend[this.properties[currentId].name] = writeable;
        }
      }

      this.notionDatabaseService.savePage(
        // The data source, not the database. Templates saved before the
        // 2025-09-03 migration have no dataSourceId until the backfill has run,
        // so fall back to resolving it from the database.
        this.template.database.dataSourceId ?? await this.resolveDataSourceId(),
        propertiesToSend,
        [...prosemirrorToNotion(this.content, this.parentHost)],
        null,
        this.cover,
      ).then((response) => {
        this.historySvc.addSingle({ notionId: response.id, createdAt: new Date(), pageUrl: this.pageUrl, host: this.parentHost, properties: propertiesToSend, templateId: this.template?.id, workspaceId: this.workspaceId });
        this.$emit('go-to-success-page', response);
      }).catch(() => {
        this.$eventBus.emit('show-toast', {
          message: 'Something went wrong. Please try again.',
          type: 'error',
        });
      }).finally(() => {
        this.sendDataLoading = false;
      });
    },
  },
  watch: {
    loading(newVal) {
      if (newVal) {
        return;
      }

      this.$nextTick(() => {
        this.evaluateBlocks();
      });
    },
    videoRecording(newVal, oldVal) {
      if (newVal === false && oldVal === true) {
        this.timer = '';
      }
    },
  },
  async created() {
    this.getAnswers();
    window.parent.postMessage({ msg: 'NOTIX_GET_PARENT_HOST' }, '*');
    window.parent.postMessage({ msg: 'NOTIX_READY' }, '*');
    window.addEventListener('message', this.handleMessage);
  },
  mounted() {
    window.addEventListener('beforeunload', (event) => {
      event.preventDefault();
      return '';
    });
  },
  beforeUnmount() {
    window.removeEventListener('message', this.handleMessage);
  },
};
</script>

<style scoped type="text/scss" lang="scss">
._thumbnail {
  position: relative;

  ._actions {
    display: none;
    position: absolute;
    top: 1rem;
    right: 1rem;
    border-radius: .4rem;
    background: white;
  }

  &:hover {
    ._actions {
      display: block;
    }
  }
}

._overlay {
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100vw;
  background: map-get($grey, 'darken-1');
  display: flex;
  align-items: center;
  justify-content: center;

  ._buttons {
    display: flex;
  }

  ._inner {
    background: white;
    border-radius: 1rem;
    padding: 2rem 2rem;
    display: flex;
    align-items: center;

    ._timer {
      font-size: 2rem;
      margin-right: 1rem;
    }
  }
}
</style>
